import { useMountEffect } from '@react-hookz/web/esm';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import { useAppSelector } from '../../../../../../hooks/hooks';
import useContext from '../../../../../../hooks/useContext';
import useModal from '../../../../../../hooks/useModal';
import {
  getFocusSelector,
  getSpecificParent,
  notUnfocusableSelector,
} from '../../../../../../utils/dom';
import { AccordionElement, Modal, SimpleButton } from '../../../../../common';
import Additional from '../../../../../common/generic/responsiveTable/filters/Additional';
import Responsive from '../../../../../common/generic/responsiveTable/filters/Responsive';
import Sort from '../../../../../common/generic/responsiveTable/filters/Sort';
import {
  ResponsiveTableReactContext,
  useResponsiveTableContext,
} from '../../../../../common/generic/responsiveTable/ResponsiveTableContext';
import ApiaComponentContext, {
  ApiaComponentContextPrimitive,
} from '../../../apiaComponent/ApiaComponentContext';
import ApiaFinderNavbar from './ApiaFinderNavbar';
import FilterTypes from './FilterTypes';

const AdditionalFiltersModal = () => {
  const context = useContext(ApiaComponentContextPrimitive);
  const { filtersConfig } = useAppSelector((globalState) => {
    const state = globalState.querySlice.queries[context.fieldId];
    return {
      filtersConfig: state?.filtersConfig,
    };
  }, shallowEqual);
  const [openpanels, setOpenpanels] = React.useState<number[]>([2]);

  const tableContext = useResponsiveTableContext();

  const additionalFilters = useAppSelector((global) => {
    const state = global.responsiveTableSlice[tableContext.name];
    return state?.filters.filter(
      (current) =>
        (current.asAdditional ||
          !state.columns.find((search) => search.name === current.column)) ??
        [],
    );
  }, shallowEqual);
  const nonAdditionalFilters = useAppSelector((global) => {
    const state = global.responsiveTableSlice[tableContext.name];
    return (
      state?.filters.filter(
        (current) =>
          !current.asAdditional &&
          !!state.columns.find((search) => search.name === current.column),
      ) ?? []
    );
  }, shallowEqual);
  const sortableColumns = useAppSelector((global) => {
    const state = global.responsiveTableSlice[tableContext.name];
    return state?.columns.filter((current) => !!current.allowSorting) ?? [];
  }, shallowEqual);
  const filters = useAppSelector((global) => {
    const state = global.responsiveTableSlice[tableContext.name];
    return state?.filters ?? [];
  }, shallowEqual);

  const ref = React.useRef<HTMLDivElement>(null);

  const { show, hide, ...modalProps } = useModal();

  useMountEffect(() => {
    if (filtersConfig?.openFiltersAutomatically) {
      show();
    }

    const existentPanels = [
      (nonAdditionalFilters.length ?? 0) > 0,
      (additionalFilters.length ?? 0) > 0,
      (sortableColumns.length ?? 0) > 0,
      !filtersConfig?.hideFilterTypes && (filters?.length ?? 0) > 0,
    ];
    const firstPanel = existentPanels.findIndex((isOpen) => isOpen === true);
    if (firstPanel !== -1) setOpenpanels([firstPanel]);
    else hide();
  });

  return (
    <>
      <SimpleButton
        size="sm"
        {...window.getVariant('outline')}
        title={window.FILTERS_LABEL}
        onClick={show}
      >
        {window.FILTERS_LABEL}
      </SimpleButton>
      <ResponsiveTableReactContext.Provider value={tableContext}>
        <ApiaComponentContext value={context}>
          <Modal
            id="AdditionalFiltersModal"
            title={window.BTN_ADDITIONAL_FILTERS}
            size="lg"
            animationDuration={0}
            variant="layout.common.modals.apiaFinder.additionalFiltersModal"
            initialFocusConfiguration={React.useMemo(
              () => ({
                scrollIntoViewOptions: { block: 'center', inline: 'center' },
              }),
              [],
            )}
            initialFocusGetter={React.useCallback(() => {
              setTimeout(() => {
                const contentParent = getSpecificParent(
                  document.activeElement as HTMLElement,
                  (parent) => parent.classList.contains('content'),
                );
                if (contentParent) contentParent.scrollTop = 0;
              }, 200);
              return ref.current?.querySelector(
                getFocusSelector(
                  `${notUnfocusableSelector}:not([aria-hidden="true"] *):not(.modal__closeButton)`,
                ),
              ) as HTMLElement;
            }, [])}
            {...modalProps}
          >
            <Box className="additionalFiltersContent" ref={ref}>
              {(nonAdditionalFilters.length ?? 0) > 0 && (
                <AccordionElement
                  fixStyleOverflowVisible
                  id="Filters"
                  title={window.FINDER_FILTERS}
                  expanded={openpanels.includes(0)}
                  onClick={() => {
                    setOpenpanels([0]);
                  }}
                  {...window.getVariant('primary')}
                >
                  <Responsive />
                </AccordionElement>
              )}
              {(additionalFilters.length ?? 0) > 0 && (
                <AccordionElement
                  fixStyleOverflowVisible
                  id="AdditionalFilters"
                  title={window.FINDER_ADDITIONAL_FILTERS}
                  expanded={openpanels.includes(1)}
                  onClick={() => {
                    setOpenpanels([1]);
                  }}
                  {...window.getVariant('primary')}
                >
                  <Additional />
                </AccordionElement>
              )}
              {(sortableColumns.length ?? 0) > 0 && (
                <AccordionElement
                  fixStyleOverflowVisible
                  id="Sortpanel"
                  title={window.FINDER_ORDER_BY}
                  expanded={openpanels.includes(2)}
                  onClick={() => {
                    setOpenpanels([2]);
                  }}
                  {...window.getVariant('primary')}
                >
                  <Sort />
                </AccordionElement>
              )}
              {!filtersConfig?.hideFilterTypes &&
                (filters?.length ?? 0) > 0 && (
                  <AccordionElement
                    fixStyleOverflowVisible
                    id="FilterTypes"
                    title={window.FINDER_FILTER_TYPES}
                    expanded={openpanels.includes(3)}
                    onClick={() => {
                      setOpenpanels([3]);
                    }}
                    {...window.getVariant('primary')}
                  >
                    <FilterTypes />
                  </AccordionElement>
                )}
            </Box>
            <Box className="additionalFilters__navbar">
              <ApiaFinderNavbar hideAdditionalFilters onSearch={hide} />
            </Box>
          </Modal>
        </ApiaComponentContext>
      </ResponsiveTableReactContext.Provider>
    </>
  );
};

export default AdditionalFiltersModal;
